"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/bottom-nav";
import {
  MapPin,
  Calendar,
  DollarSign,
  Loader2,
  Search,
  Briefcase,
  Check,
  X,
} from "lucide-react";
import type { Job } from "@/lib/types";

const TRADE_FILTERS = [
  "All",
  "Steel Fixer",
  "Formworker",
  "Labourer",
  "Concreter",
  "Carpenter",
  "Scaffolder",
  "Other",
];

const TRADE_ID_TO_LABEL: Record<string, string> = {
  "steel-fixer": "Steel Fixer",
  "formworker": "Formworker",
  "labourer": "Labourer",
  "concreter": "Concreter",
  "carpenter": "Carpenter",
  "scaffolder": "Scaffolder",
  "other": "Other",
};

function tradeLabelFromId(id: string): string {
  return TRADE_ID_TO_LABEL[id] || id;
}

interface SwipeableJobCardProps {
  job: Job;
  hasApplied: boolean;
  onApply: (job: Job) => void;
  onClick: (job: Job) => void;
}

function SwipeableJobCard({ job, hasApplied, onApply, onClick }: SwipeableJobCardProps) {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (hasApplied) return;
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swiping || hasApplied) return;
    currentX.current = e.touches[0].clientX;
    const diff = currentX.current - startX.current;
    // Only allow right swipe (positive diff)
    if (diff > 0) {
      setSwipeX(Math.min(diff, 150));
    }
  };

  const handleTouchEnd = () => {
    if (!swiping || hasApplied) return;
    setSwiping(false);
    
    // If swiped more than 100px, apply
    if (swipeX > 100) {
      onApply(job);
    }
    
    // Reset
    setSwipeX(0);
  };

  const swipeProgress = Math.min(swipeX / 100, 1);
  const backgroundColor = hasApplied 
    ? "transparent" 
    : `rgba(34, 197, 94, ${swipeProgress * 0.1})`;

  return (
    <div className="relative">
      {/* Swipe indicator background */}
      {!hasApplied && swipeX > 0 && (
        <div 
          className="absolute inset-0 rounded-xl flex items-center justify-start pl-6 pointer-events-none"
          style={{ 
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            opacity: swipeProgress 
          }}
        >
          <Check className="h-8 w-8 text-green-600" />
          <span className="ml-2 text-green-700 font-semibold">
            {swipeProgress >= 1 ? "Release to apply" : "Swipe to apply"}
          </span>
        </div>
      )}
      
      <Card
        className="cursor-pointer active:scale-[0.98] transition-transform touch-pan-y"
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? 'none' : 'transform 0.2s ease-out',
          backgroundColor,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => !swiping && onClick(job)}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-sm shrink-0 py-1 px-3">
                  {tradeLabelFromId(job.trade)}
                </Badge>
                {hasApplied && (
                  <Badge className="text-sm bg-green-600 shrink-0 py-1 px-3">
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Applied
                  </Badge>
                )}
              </div>
              <h3 className="font-bold text-lg truncate">{job.title}</h3>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-base">
            {job.suburb && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {job.suburb}
              </span>
            )}
            {(job.rate_min || job.rate_max) && (
              <span className="flex items-center gap-1.5 font-semibold text-foreground">
                <DollarSign className="h-4 w-4" />
                {job.rate_min && job.rate_max
                  ? `$${job.rate_min}–$${job.rate_max}`
                  : job.rate_min
                  ? `From $${job.rate_min}`
                  : `Up to $${job.rate_max}`}
                {job.rate_type === "hourly" ? "/hr" : "/day"}
              </span>
            )}
            {job.start_date && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {new Date(job.start_date).toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            )}
            {job.workers_needed > 1 && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Briefcase className="h-4 w-4" />
                {job.workers_needed} workers
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function WorkerJobsPage() {
  const supabase = createClient();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTrade, setActiveTrade] = useState("All");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [applying, setApplying] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setUserId(user.id);

      // Fetch active jobs
      const { data } = await supabase
        .from("jobs")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (data) setJobs(data);

      // Fetch already-applied jobs
      if (user) {
        const { data: matches } = await supabase
          .from("matches")
          .select("job_id")
          .eq("worker_id", user.id);

        if (matches) {
          setAppliedJobs(new Set(matches.map((m: { job_id: string }) => m.job_id)));
        }
      }

      setLoading(false);
    }
    load();
  }, [supabase]);

  const filteredJobs =
    activeTrade === "All"
      ? jobs
      : jobs.filter(
          (j) => tradeLabelFromId(j.trade).toLowerCase() === activeTrade.toLowerCase()
        );

  async function handleApply(job: Job) {
    if (!userId || appliedJobs.has(job.id)) return;
    setApplying(true);

    // Haptic feedback (if supported)
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    const { error } = await supabase.from("matches").insert({
      job_id: job.id,
      worker_id: userId,
      status: "applied",
      contractor_action: "pending",
      worker_action: "accepted",
    });

    if (!error) {
      setAppliedJobs((prev) => new Set(prev).add(job.id));
    }

    setApplying(false);
    setSelectedJob(null);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-screen-sm items-center justify-between px-4">
          <h1 className="text-xl font-bold">Available Jobs</h1>
          <Link href="/dashboard" className="text-xl font-bold tracking-tight">
            RateRight
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Trade filters — horizontal scroll */}
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 scrollbar-hide">
          {TRADE_FILTERS.map((trade) => (
            <button
              key={trade}
              onClick={() => setActiveTrade(trade)}
              className={`shrink-0 rounded-full px-5 py-3 text-base font-semibold transition-all active:scale-95 min-h-[48px] ${
                activeTrade === trade
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {trade}
            </button>
          ))}
        </div>

        {/* Swipe hint */}
        {filteredJobs.length > 0 && !loading && (
          <div className="mt-4 mb-2 text-center">
            <p className="text-sm text-muted-foreground">
              👉 Swipe right to quick apply • Tap to view details
            </p>
          </div>
        )}

        {/* Job list */}
        {filteredJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="h-16 w-16 text-muted-foreground/50" />
            <p className="mt-4 text-xl font-semibold">No jobs found</p>
            <p className="mt-2 text-base text-muted-foreground">
              {activeTrade === "All"
                ? "No jobs posted yet. Check back soon."
                : `No ${activeTrade} jobs right now. Try another trade.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {filteredJobs.map((job) => (
              <SwipeableJobCard
                key={job.id}
                job={job}
                hasApplied={appliedJobs.has(job.id)}
                onApply={handleApply}
                onClick={setSelectedJob}
              />
            ))}
          </div>
        )}
      </main>

      {/* Job Detail Sheet (bottom sheet style) */}
      {selectedJob && (
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedJob(null)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-background pb-24">
            {/* Handle */}
            <div className="sticky top-0 flex justify-center py-4 bg-background rounded-t-3xl">
              <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
            </div>

            <div className="px-6 pb-6">
              <Badge variant="secondary" className="mb-3 text-sm py-1.5 px-3">
                {tradeLabelFromId(selectedJob.trade)}
              </Badge>
              <h2 className="text-2xl font-bold">{selectedJob.title}</h2>

              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-base">
                {selectedJob.suburb && (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-5 w-5" />
                    {selectedJob.site_address || selectedJob.suburb}
                  </span>
                )}
                {(selectedJob.rate_min || selectedJob.rate_max) && (
                  <span className="flex items-center gap-2 font-bold text-lg">
                    <DollarSign className="h-5 w-5" />
                    {selectedJob.rate_min && selectedJob.rate_max
                      ? `$${selectedJob.rate_min}–$${selectedJob.rate_max}`
                      : selectedJob.rate_min
                      ? `From $${selectedJob.rate_min}`
                      : `Up to $${selectedJob.rate_max}`}
                    {selectedJob.rate_type === "hourly" ? "/hr" : "/day"}
                  </span>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-base text-muted-foreground">
                {selectedJob.start_date && (
                  <span className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Starts{" "}
                    {new Date(selectedJob.start_date).toLocaleDateString("en-AU", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                )}
                {selectedJob.duration && (
                  <span>Duration: {selectedJob.duration}</span>
                )}
                {selectedJob.workers_needed > 1 && (
                  <span className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    {selectedJob.workers_needed} workers needed
                  </span>
                )}
              </div>

              {selectedJob.description && (
                <div className="mt-6">
                  <h3 className="text-base font-bold mb-2">About the Job</h3>
                  <p className="text-base text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {selectedJob.description}
                  </p>
                </div>
              )}

              {selectedJob.certifications_required?.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-base font-bold mb-2">Required Certs</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.certifications_required.map((cert) => (
                      <Badge key={cert} variant="outline" className="text-sm py-1.5 px-3">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Bottom CTA */}
            <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
              <div className="px-6 py-4">
                {appliedJobs.has(selectedJob.id) ? (
                  <Button disabled className="w-full h-16 text-lg font-semibold bg-green-600">
                    <Check className="mr-2 h-6 w-6" />
                    Already Applied
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleApply(selectedJob)}
                    className="w-full h-16 text-lg font-semibold"
                    disabled={applying}
                  >
                    {applying ? (
                      <>
                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        Apply with One Tap
                        <Check className="ml-2 h-6 w-6" />
                      </>
                    )}
                  </Button>
                )}
                <p className="mt-3 text-center text-sm text-muted-foreground font-medium">
                  Always free for workers. No fees ever.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav userType="worker" />
    </div>
  );
}
